/* eslint-disable jsdoc/require-jsdoc */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactElement, useEffect, useRef, useState } from 'react';
import { BeatLoader } from 'react-spinners';
import CanvasJSReact from '@canvasjs/react-charts';
import useDimensions from '../hooks/useDimension';
import { useData } from '../context/data.context';
import { COLORS, GRAPH_COLORS } from '../constants/global-constants';
import ParameterLegend from './ParameterLegend';
import { ParametersType } from '../constants/telosair-interfaces';
import { getParameterByName } from '../_helpers/globalHelper';
import { createColorObject, generateBoxPlotData } from '../_helpers/graph';
import Deque from '../classes/deque';
import { Filters, getAppliedFilter, setAppliedFilter } from '../services/localstorage.service';

const ZOOMED_FONT_SIZE = 18;
const DEFAULT_FONT_SIZE = 15;

/** X-axis can show one box group per device or one per parameter. */
export type AxisMode = 'device' | 'parameter';

const defaultAxisY = {
  lineColor: GRAPH_COLORS.grid,
  gridColor: GRAPH_COLORS.grid,
  labelFontSize: DEFAULT_FONT_SIZE
};

type Props = {
  isZoomed?: boolean;
  chartGraphData: Record<string, Deque>;
  selectedParameters: ParametersType[];
  isDataLoading: boolean;
  selectedDevices: { label: string; value: number }[];
  localParameterList: any[];
  /** Whether devices or parameters sit on the x-axis (toggled from the panel header). */
  axisMode: AxisMode;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { CanvasJSChart } = CanvasJSReact;

/**
 * Spreads boxes that share the same x slot symmetrically around the slot centre.
 * @param index - Index of the box within the slot.
 * @param length - Number of boxes in the slot.
 * @param point - Slot centre (integer x position).
 * @param step - Distance between adjacent boxes in the slot.
 * @returns - The x position for the box.
 */
function transformValue(index: number, length: number, point: number, step: number): number {
  const range = (length - 1) / 2;
  return (index - range) * step + point;
}

/**
 * Linearly maps a value into a 0-100% range.
 * @param value - The value to normalize.
 * @param range - Min/max range of the parameter.
 * @returns - The normalized value.
 */
function normalizeValue(value: number, range: { min: number; max: number } | undefined): number {
  if (
    !range ||
    !Number.isFinite(range.min) ||
    !Number.isFinite(range.max) ||
    range.max === range.min
  ) {
    return 50;
  }
  return ((value - range.min) / (range.max - range.min)) * 100;
}

/**
 * React component for BoxAndWhiskerChart.
 * @param props - Properties of BoxAndWhiskerChart.
 * @returns - React component for BoxAndWhiskerChart.
 */
export default function BoxAndWhiskerChart(props: Props): ReactElement {
  const {
    chartGraphData,
    selectedParameters,
    isDataLoading,
    selectedDevices,
    localParameterList,
    isZoomed,
    axisMode
  } = props;
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const { parameters } = useData();
  const dimension = useDimensions(chartContainerRef);
  const fontSize = isZoomed ? ZOOMED_FONT_SIZE : DEFAULT_FONT_SIZE;

  // Active legend selections - restored from the last use, intersected with the
  // currently available parameters/devices.
  const [activeParameters, setActiveParameters] = useState<ParametersType[]>(() => {
    const stored = (getAppliedFilter(Filters.boxPlotParameters)?.data as ParametersType[]) || [];
    const restored = stored.filter((value) => selectedParameters.includes(value));
    if (restored.length) return restored;
    return selectedParameters.length ? [selectedParameters[0]] : [];
  });
  const [activeDevices, setActiveDevices] = useState<number[]>(() => {
    const validValues = selectedDevices.map((device) => device.value);
    const stored = (getAppliedFilter(Filters.boxPlotDevices)?.data as number[]) || [];
    const restored = stored.filter((value) => validValues.includes(value));
    return restored.length ? restored : validValues;
  });

  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState<any>({ data: [] });

  // Persist legend selections so they are retained across visits/refreshes.
  useEffect(() => {
    setAppliedFilter({ key: Filters.boxPlotParameters, data: activeParameters });
  }, [activeParameters]);
  useEffect(() => {
    setAppliedFilter({ key: Filters.boxPlotDevices, data: activeDevices });
  }, [activeDevices]);

  // Drop legend entries that are no longer part of the top-level selection.
  useEffect(() => {
    setActiveParameters((prev) => {
      const valid = prev.filter((value) => selectedParameters.includes(value));
      if (valid.length) return valid.length === prev.length ? prev : valid;
      return selectedParameters.length ? [selectedParameters[0]] : [];
    });
  }, [selectedParameters]);
  useEffect(() => {
    setActiveDevices((prev) => {
      const validValues = selectedDevices.map((device) => device.value);
      const valid = prev.filter((value) => validValues.includes(value));
      if (valid.length) return valid.length === prev.length ? prev : valid;
      return validValues;
    });
  }, [selectedDevices]);

  // The dual y-axis (device mode) can only show two parameters.
  useEffect(() => {
    if (axisMode === 'device') {
      setActiveParameters((prev) => (prev.length > 2 ? prev.slice(prev.length - 2) : prev));
    }
  }, [axisMode]);

  const parameterColors = createColorObject(selectedParameters, localParameterList);
  const deviceColors: Record<number, string> = {};
  selectedDevices.forEach((device, index) => {
    deviceColors[device.value] = COLORS[index % COLORS.length];
  });

  /**
   * Tooltip renderer - always shows the real (un-normalized) values.
   * @param e - CanvasJS tooltip event.
   * @returns - HTML string for the tooltip.
   */
  const toolTipContentFormatter = (e: any): string => {
    const entry = e?.entries?.[0];
    if (!entry?.dataPoint) return '';
    const dp = entry.dataPoint;
    const values: number[] = (dp.rawY && dp.rawY.length ? dp.rawY : dp.y) || [];
    if (values.length < 5) return dp.label || '';
    // generateBoxPlotData order: [min, Q1, Q3, max, median]
    const [minV, q1V, q3V, maxV, medianV] = values;
    const fmt = (value: number): string => (typeof value === 'number' ? value.toFixed(2) : '-');
    return `<strong>${dp.label || ''}</strong><br/>Max: ${fmt(maxV)}<br/>Q3: ${fmt(
      q3V
    )}<br/>Median: ${fmt(medianV)}<br/>Q1: ${fmt(q1V)}<br/>Min: ${fmt(minV)}`;
  };

  useEffect(() => {
    // Preserve the top-level order for stable x-slots.
    const orderedParameters = selectedParameters.filter((parameter) =>
      activeParameters.includes(parameter)
    );
    const orderedDevices = selectedDevices.filter((device) =>
      activeDevices.includes(device.value)
    );

    // Box sizing: thinner boxes when many devices/parameters are selected so
    // they never overlap. spreadStep keeps a slot's boxes inside the slot.
    const numSlots = axisMode === 'device' ? orderedDevices.length : orderedParameters.length;
    const boxesPerSlot =
      axisMode === 'device' ? orderedParameters.length : orderedDevices.length;
    const spreadStep = boxesPerSlot > 1 ? Math.min(0.2, 0.8 / (boxesPerSlot - 1)) : 0;
    const plotWidth = dimension.width || 600;
    const pxPerUnit = numSlots > 0 ? plotWidth / (numSlots + 1) : plotWidth;
    const computedBoxWidth =
      boxesPerSlot > 1
        ? Math.max(3, Math.min(24, Math.floor(spreadStep * pxPerUnit * 0.8)))
        : Math.max(4, Math.min(28, Math.floor(pxPerUnit * 0.45)));

    const tempData: any[] = [];
    const axisLabels: string[] = [];
    const axisConfig: any = {};

    if (axisMode === 'device') {
      // X-axis = devices, one series (colour) per parameter, dual y-axis.
      orderedDevices.forEach((device) => axisLabels.push(`#${device.value}`));

      orderedParameters.forEach((parameter, parameterIndex) => {
        const currentParameter = parameters.find(
          (current) => current.parameterName === parameter
        );
        if (!currentParameter) return;

        const yAxis = {
          ...defaultAxisY,
          tickColor: parameterColors[parameter],
          labelFontColor: parameterColors[parameter],
          titleFontColor: GRAPH_COLORS.level,
          titleFontSize: fontSize,
          labelFontSize: fontSize,
          title: currentParameter.shortName,
          labelFormatter: (event: any) => {
            if (event.value === 0) return currentParameter.shortName;
            return event.value.toFixed(2);
          }
        };
        if (parameterIndex === 0) {
          axisConfig.axisY = yAxis;
        } else {
          axisConfig[`axisY${parameterIndex + 1}`] = yAxis;
        }

        const dataPoints = orderedDevices.map((device, deviceSlot) => {
          const deque = chartGraphData[`${device.value}:${parameter}`];
          const deviceData = deque ? deque.toArray() : [];
          return {
            y: deviceData.length > 0 ? generateBoxPlotData(deviceData) : [],
            x: transformValue(parameterIndex, orderedParameters.length, deviceSlot, spreadStep),
            label: `#${device.value} - ${currentParameter.shortName}`
          };
        });

        tempData.push({
          type: 'boxAndWhisker',
          upperBoxColor: 'transparent',
          lowerBoxColor: 'transparent',
          axisYIndex: parameterIndex,
          showInLegend: false,
          color: parameterColors[parameter],
          axisYType: parameterIndex === 0 ? 'primary' : 'secondary',
          dataPoints
        });
      });
    } else {
      // X-axis = parameters, one series (colour) per device, normalized y-axis.
      orderedParameters.forEach((parameter) => {
        axisLabels.push(getParameterByName(parameter, parameters)?.shortName || parameter);
      });

      // Per-parameter min/max so each parameter can be scaled to 0-100%.
      const parameterRanges: Record<string, { min: number; max: number }> = {};
      orderedParameters.forEach((parameter) => {
        let min = Infinity;
        let max = -Infinity;
        orderedDevices.forEach((device) => {
          const deque = chartGraphData[`${device.value}:${parameter}`];
          const deviceData = deque ? deque.toArray() : [];
          deviceData.forEach((point: number[]) => {
            const value = point[1];
            if (value !== null && value !== undefined && !Number.isNaN(value)) {
              if (value < min) min = value;
              if (value > max) max = value;
            }
          });
        });
        parameterRanges[parameter] = { min, max };
      });

      axisConfig.axisY = {
        ...defaultAxisY,
        minimum: 0,
        maximum: 105,
        interval: 25,
        tickColor: GRAPH_COLORS.level,
        labelFontColor: GRAPH_COLORS.level,
        titleFontColor: GRAPH_COLORS.level,
        titleFontSize: fontSize,
        labelFontSize: fontSize,
        title: 'Normalized (%)',
        labelFormatter: (event: any) => `${event.value}%`
      };

      orderedDevices.forEach((device, deviceIndex) => {
        const dataPoints = orderedParameters.map((parameter, parameterSlot) => {
          const deque = chartGraphData[`${device.value}:${parameter}`];
          const deviceData = deque ? deque.toArray() : [];
          const rawBox = deviceData.length > 0 ? generateBoxPlotData(deviceData) : [];
          const shortName = getParameterByName(parameter, parameters)?.shortName || parameter;
          return {
            y: rawBox.map((value) => normalizeValue(value, parameterRanges[parameter])),
            rawY: rawBox,
            x: transformValue(deviceIndex, orderedDevices.length, parameterSlot, spreadStep),
            label: `#${device.value} - ${shortName}`
          };
        });

        tempData.push({
          type: 'boxAndWhisker',
          upperBoxColor: 'transparent',
          lowerBoxColor: 'transparent',
          showInLegend: false,
          color: deviceColors[device.value],
          dataPoints
        });
      });
    }

    // Trailing series provides the x-axis category labels.
    tempData.push({ dataPoints: axisLabels.map((label) => ({ label })) });

    setOptions({
      animationEnabled: true,
      maintainAspectRatio: true,
      backgroundColor: 'transparent',
      exportEnabled: false,
      height: dimension.height,
      dataPointWidth: computedBoxWidth,
      title: '',
      toolTip: { contentFormatter: toolTipContentFormatter },
      axisX: {
        lineColor: GRAPH_COLORS.grid,
        tickColor: GRAPH_COLORS.grid,
        gridColor: GRAPH_COLORS.grid,
        labelFontColor: GRAPH_COLORS.level,
        labelFontSize: fontSize
      },
      ...axisConfig,
      data: tempData
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    parameters,
    chartGraphData,
    selectedParameters,
    selectedDevices,
    activeDevices,
    activeParameters,
    axisMode,
    localParameterList,
    isZoomed,
    dimension
  ]);

  /**
   * Get the CSS class for a legend entry based on its active status.
   * @param value - Legend value.
   * @param type - Legend type.
   * @returns - The CSS class.
   */
  const parameterClass = (value: string, type?: 'device' | 'parameter'): string => {
    if (type === 'device') {
      return activeDevices.includes(Number(value)) ? 'text-white' : 'text-gray-400';
    }
    return activeParameters.includes(value as ParametersType) ? 'text-white' : 'text-gray-400';
  };

  /**
   * Handles parameter legend click.
   * @param parameter - Clicked parameter.
   * @param active - Is parameter active.
   */
  const handleParameterClick = (parameter: ParametersType, active: boolean): void => {
    if (active) {
      setActiveParameters((prev) => prev.filter((item) => item !== parameter));
    } else {
      setActiveParameters((prev) => {
        // Device mode keeps a dual y-axis, so it allows at most two parameters.
        const maxParameters = axisMode === 'device' ? 2 : selectedParameters.length;
        if (prev.length >= maxParameters) {
          return [...prev.slice(prev.length - maxParameters + 1), parameter];
        }
        return [...prev, parameter];
      });
    }
  };

  /**
   * Handles device legend click.
   * @param device - Clicked device.
   * @param active - Is device active.
   */
  const handleDeviceClick = (device: number, active: boolean): void => {
    if (active) {
      setActiveDevices((prev) => prev.filter((item) => item !== device));
    } else {
      setActiveDevices((prev) => [...prev, device]);
    }
  };

  useEffect(() => {
    let timeout: any;
    if (dimension.height) {
      setLoading(true);
      timeout = setTimeout(() => {
        setLoading(false);
      }, 500);
    }
    return () => clearTimeout(timeout);
  }, [dimension]);

  return (
    <div className="flex flex-col w-full justify-between h-full">
      <div className="flex justify-between items-center w-full gap-5">
        <div className="flex flex-wrap mt-2 gap-3">
          {selectedParameters.map((parameter) => {
            const active = activeParameters.includes(parameter);
            return (
              <ParameterLegend
                className={parameterClass(parameter, 'parameter')}
                key={parameter}
                name={getParameterByName(parameter, parameters)?.shortName || 'N/A'}
                color={parameterColors[parameter]}
                active={active}
                parameter={parameter}
                handleParameterClick={handleParameterClick}
              />
            );
          })}
        </div>
        <div className="flex flex-wrap mt-2 gap-3">
          {selectedDevices.map((device) => {
            const active = activeDevices.includes(device.value);
            return (
              <button
                key={device.value}
                type="button"
                style={{
                  opacity: active ? 1 : 0.5
                }}
                onClick={() => handleDeviceClick(device.value, active)}
                className={`flex gap-1 items-center ${parameterClass(
                  String(device.value),
                  'device'
                )} cursor-pointer `}
              >
                <span
                  className="material-icons text-sm"
                  style={{
                    color: axisMode === 'parameter' ? deviceColors[device.value] : '#D0D5DD'
                  }}
                >
                  square
                </span>
                <div style={{ color: active ? '#fff' : '#D0D5DD' }} className="text-sm font-normal">
                  {device.label}
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <div ref={chartContainerRef} className="flex flex-1 items-center justify-center mt-4">
        {(isDataLoading || loading) && <BeatLoader color="#36d7b7" />}
        {!isDataLoading && !loading && (
          <div className="flex flex-1 items-end">
            <CanvasJSChart options={options} />
          </div>
        )}
      </div>
    </div>
  );
}
